import { useHandler } from "@redotech/react-util/hook";
import {
  CustomerTagsSegmentListMultipleItemMembershipOperator,
  CustomerTagsSegmentListMultipleItemMembershipOperator as CustomerTagsSegmentListMultipleItemMembershipOperatorEnum,
  SegmentListMultipleItemMembershipOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoCustomerTagsSegmentSchema,
  RcoCustomerTagsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { CustomerTagsSelector } from "./customer-tags-selector";

export const CustomerTagsInspectorContent = memo(
  function CustomerTagsInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCustomerTagsSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.tags.map((tag, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={tag} />
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  operator: RcoCustomerTagsSegmentSchema["operator"],
) => {
  switch (operator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      return t("Customer has at least one tag in the list:");
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      return t("Customer does not have any tag in the list:");
    default:
      assertNever(operator);
  }
};

export function customerTagsSegmentListMultipleItemMembershipOperatorToText(): Record<
  CustomerTagsSegmentListMultipleItemMembershipOperator,
  string
> {
  return {
    [SegmentListMultipleItemMembershipOperator.ANY_IN]: t("at least one"),
    [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
  };
}

export const EditableCustomerTagsInspectorContent = memo(
  function EditableCustomerTagsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoCustomerTagsSegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorSelected = useHandler(
      (operator: CustomerTagsSegmentListMultipleItemMembershipOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleTagsSelected = useHandler((tags: string[]) => {
      const updatedSegment = { ...segment, tags };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Customer has")}
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<CustomerTagsSegmentListMultipleItemMembershipOperator>
            availableOptions={
              CustomerTagsSegmentListMultipleItemMembershipOperatorEnum
            }
            onOperatorSelected={handleOperatorSelected}
            operatorToText={customerTagsSegmentListMultipleItemMembershipOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("tag in the list:")}
        </Text>
        <CustomerTagsSelector
          onTagsSelected={handleTagsSelected}
          selectedTags={segment.tags}
        />
      </>
    );
  },
);
